import React, { useState, useEffect, useRef } from "react";
import { Chess } from "chess.js";
import "./chess.css";
import "./chessWeb.css";
import logo from "../../../assets/images/coinlogo.png";
import useUserInfo from "../../../Hooks/useUserInfo";
import cancelIcon from "../../../assets/Task/cancelicon.svg";
import { userGameRewards } from "../../../apis/user";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../../Task/Task";
import { logPageView } from "../../../components/analytics";
import { io } from "socket.io-client";
// import { v4 as uuidv4 } from "uuid";
import { Base_Url } from "../../../apis/baseurl";

// Chess piece Unicode symbols
const PIECES = {
  w: {
    p: "♟︎", // Using filled pawns with variation selector for white
    r: "♜︎", // Using filled rooks with variation selector for white
    n: "♞︎", // Using filled knights with variation selector for white
    b: "♝︎", // Using filled bishops with variation selector for white
    q: "♛︎", // Using filled queens with variation selector for white
    k: "♚︎", // Using filled kings with variation selector for white
  },
  b: {
    p: "♟",
    r: "♜",
    n: "♞",
    b: "♝",
    q: "♛",
    k: "♚",
  },
};

const ChessGameWeb = () => {
  const { userDetails, updateUserInfo, watchScreen, updatewatchScreenInfo } =
    useUserInfo();
  const userId = userDetails?.userDetails?.telegramId;
  const [socket, setSocket] = useState(null);
  const [game, setGame] = useState(new Chess());
  const [board, setBoard] = useState([]);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [status, setStatus] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [capturedPieces, setCapturedPieces] = useState({ w: [], b: [] });
  const [points, setPoints] = useState(0);
  const [playerColor, setPlayerColor] = useState(null); // 'w' or 'b'
  const [roomId, setRoomId] = useState(null);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [gameStatus, setGameStatus] = useState("waiting"); // 'waiting', 'playing', 'finished'
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [movablePieces, setMovablePieces] = useState([]);
  const playClick = useAudio();
  const [copySuccess, setCopySuccess] = useState("");
  const [inputRoomId, setInputRoomId] = useState("");
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const waitingTimerRef = useRef(null);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [whiteTime, setWhiteTime] = useState(600); // 10 minutes in seconds
  const [blackTime, setBlackTime] = useState(600); // 10 minutes in seconds
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [pointsAwarded, setPointsAwarded] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const apiCallInProgress = useRef(false);

  // Handle back button with confirmation
  const handleBackButton = () => {
    if (gameStatus === "playing" && players.length === 2) {
      // Show confirmation dialog for active games
      setShowLeaveConfirmation(true);
    } else {
      // Leave immediately if not in active game
      leaveGame();
    }
  };

  // Leave game function
  const leaveGame = () => {
    setRoomId(null);
    setGameStatus("waiting");
    setInputRoomId("");
    setGame(null);
    setBoard([]);
    setSelectedSquare(null);
    setGameStatus("waiting");
    setWinner(null);
    setGameOver(false);
    setPlayers([]);
    setPlayerColor(null);
    setIsMyTurn(false);
    setWhiteTime(600);
    setBlackTime(600);
    setTimerActive(false);
    setShowInfoPopup(false);
    setShowLeaveConfirmation(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (socket && roomId) {
      socket.emit("leaveRoom", { roomId: roomId });
    }
  };

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(Base_Url.base_url, {
      auth: {
        token: localStorage.getItem("token"), // Assuming you store the token in localStorage
      },
    });
    setSocket(newSocket);
    // Add connection status logs
    newSocket.on("connect", () => {
      // Connected to server
    });

    newSocket.on("connect_error", (error) => {
      // Socket connection error
    });

    return () => newSocket.disconnect();
  }, []);

  // Handle page unload to notify server of disconnection
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socket && roomId && gameStatus === "playing") {
        socket.emit("playerDisconnecting", { roomId });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [socket, roomId, gameStatus]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    socket.on("chessGameState", (gameState) => {
      // Received chess game state

      // Update local game state based on server data
      if (gameState.fen) {
        const newGame = new Chess(gameState.fen);
        setGame(newGame);
        updateBoard(newGame);
      }
      if (gameState.players) {
        setPlayers(gameState.players);
        // Determine player color based on position in players array
        const playerIndex = gameState.players.indexOf(socket.id);
        setPlayerColor(playerIndex === 0 ? "w" : "b");
      }
      if (gameState.currentPlayer) {
        setCurrentPlayer(gameState.currentPlayer);
        setIsMyTurn(gameState.currentPlayer === socket.id);
      }
      if (gameState.gameStatus) {
        setGameStatus(gameState.gameStatus);
      }

      if (gameState.roomId) setRoomId(gameState.roomId);
      if (gameState.lastMove) setLastMove(gameState.lastMove);

      // Sync timers between players
      if (gameState.whiteTime !== undefined) {
        setWhiteTime(gameState.whiteTime);
      }
      if (gameState.blackTime !== undefined) {
        setBlackTime(gameState.blackTime);
      }

      // Handle game status changes
      if (gameState.gameStatus === "playing" && players.length === 2) {
        setIsMyTurn(gameState.currentPlayer === socket.id);
        setCurrentPlayer(gameState.currentPlayer);

        // Ensure timer is active when turn changes
        if (gameState.currentPlayer !== currentPlayer) {
          setTimerActive(true);
        }
      }

      // Handle game over state
      if (gameState.gameStatus === "finished") {
        setGameOver(true);

        if (gameState.winner === "Draw" || gameState.winner === "draw") {
          // Handle draw scenario
          setWinner("Draw");
          if (!pointsAwarded) {
            const drawPoints = 0;
            setPoints(drawPoints);
            handleApiResponse(drawPoints.toString());
          }
        } else if (gameState.winner === "Disconnected") {
          // Handle disconnection
          setWinner("Disconnected");
          if (!pointsAwarded) {
            const disconnectPoints = 0;
            setPoints(disconnectPoints);
            handleApiResponse(disconnectPoints.toString());
          }
        } else if (gameState.winner) {
          // Convert socket ID back to color for display
          const winnerPlayerIndex = gameState.players.indexOf(gameState.winner);
          const winnerColor = winnerPlayerIndex === 0 ? "w" : "b";
          setWinner(winnerColor);

          // Calculate score based on winner - only if points haven't been awarded yet
          if (!pointsAwarded) {
            if (gameState.winner === socket.id) {
              // Current player won
              const winPoints = userDetails?.earlyUsersLimitReached
                ? 1000
                : 2000;
              setPoints(winPoints);
              handleApiResponse(winPoints.toString());
            } else {
              // Current player lost
              const losePoints = 0;
              setPoints(losePoints);
              handleApiResponse(losePoints.toString());
            }
          }
        } else {
          // Fallback for no winner specified
          setWinner("Draw");
          if (!pointsAwarded) {
            const drawPoints = 0;
            setPoints(drawPoints);
            handleApiResponse(drawPoints.toString());
          }
        }
      }
    });

    socket.on("playerJoined", ({ players, roomId }) => {
      setPlayers(players);
      setRoomId(roomId);

      // If we have 2 players, start the game
      if (players.length === 2) {
        const newGame = new Chess(); // Start with a fresh game
        const initialGameState = {
          fen: newGame.fen(),
          players: players,
          currentPlayer: players[0], // First player (white) starts
          gameStatus: "playing",
          roomId: roomId,
          lastMove: null,
        };

        // Update local game status to playing
        setGameStatus("playing");

        // Set player color based on whether this player created the room or joined it
        // The first player (room creator) gets white, second player (joiner) gets black
        const isRoomCreator = players[0] === socket.id;
        setPlayerColor(isRoomCreator ? "w" : "b");

        // Set turn status (white always starts in chess)
        setIsMyTurn(isRoomCreator);

        // Reset points and points awarded flag for new game
        setPoints(0);
        setPointsAwarded(false);
        apiCallInProgress.current = false;

        // Initialize timer state
        setWhiteTime(600);
        setBlackTime(600);

        // Start the timer
        setTimerActive(true);

        // Emit the initial game state to all players
        socket.emit("updateChessGameState", {
          ...initialGameState,
          whiteTime: 600,
          blackTime: 600,
        });
      }
    });

    socket.on("playerLeft", () => {
      // Only show disconnect message if game wasn't already finished
      if (gameStatus !== "finished") {
        setWinner("Disconnected");
        setGameStatus("finished");
        setGameOver(true);

        // No points awarded for opponent disconnection
        setPoints(0);

        // Stop timer
        setTimerActive(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        // Send game over state to server
        const disconnectedGameState = {
          fen: game.fen(),
          players: players,
          currentPlayer: currentPlayer,
          gameStatus: "finished",
          roomId: roomId,
          winner: "Disconnected",
          lastMove: lastMove,
          whiteTime: whiteTime,
          blackTime: blackTime,
          points: {
            winner: 0,
            loser: 0,
            winnerSocketId: null,
            loserSocketId: null,
          },
        };
        socket.emit("updateChessGameState", disconnectedGameState);
      }
    });

    socket.on("roomFull", () => {
      // Check if we're trying to join our own room (which might cause a roomFull error on some server implementations)
      if (inputRoomId && inputRoomId === roomId) {
        // No need to show an alert, just clear the input
        setInputRoomId("");
      } else {
        alert("Room is full. Please try another room.");
      }
    });

    socket.on("invalidRoomId", (message) => {
      alert(message || "Invalid room ID format");
    });

    return () => {
      socket.off("chessGameState");
      socket.off("playerJoined");
      socket.off("playerLeft");
      socket.off("roomFull");
      socket.off("invalidRoomId");
    };
  }, [socket, players, pointsAwarded]);

  // Setup waiting timer when room is created/joined
  useEffect(() => {
    // Clear any existing timer
    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
    // Set a new timer if we're in waiting state with a room ID
    if (gameStatus === "waiting" && roomId) {
      waitingTimerRef.current = setTimeout(() => {
        setShowTimeoutPopup(true);
      }, 60000); // 120 seconds
    }

    // Clear timer when game starts or component unmounts
    return () => {
      if (waitingTimerRef.current) {
        clearTimeout(waitingTimerRef.current);
      }
    };
  }, [gameStatus, roomId]);

  // Clear timer when game starts
  useEffect(() => {
    if (gameStatus === "playing" && waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current);
      waitingTimerRef.current = null;
    }
  }, [gameStatus]);

  // Timer logic for game time limit - simplified and more reliable
  useEffect(() => {
    // Clear any existing timer first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (gameStatus === "playing" && timerActive && !gameOver) {
      timerRef.current = setInterval(() => {
        // Update the timer for the current player and sync with server
        if (currentPlayer === socket?.id) {
          if (playerColor === "w") {
            setWhiteTime((prev) => {
              if (prev <= 1) {
                // White player ran out of time - Black wins
                handleTimeOut("b");
                return 0;
              }
              const newTime = prev - 1;

              // Sync timer with server for real-time updates
              if (socket && roomId) {
                socket.emit("updateChessGameState", {
                  roomId: roomId,
                  fen: game.fen(),
                  players: players,
                  currentPlayer: currentPlayer,
                  gameStatus: "playing",
                  whiteTime: newTime,
                  blackTime: blackTime,
                  lastMove: lastMove,
                });
              }

              return newTime;
            });
          } else {
            setBlackTime((prev) => {
              if (prev <= 1) {
                // Black player ran out of time - White wins
                handleTimeOut("w");
                return 0;
              }
              const newTime = prev - 1;

              // Sync timer with server for real-time updates
              if (socket && roomId) {
                socket.emit("updateChessGameState", {
                  roomId: roomId,
                  fen: game.fen(),
                  players: players,
                  currentPlayer: currentPlayer,
                  gameStatus: "playing",
                  whiteTime: whiteTime,
                  blackTime: newTime,
                  lastMove: lastMove,
                });
              }

              return newTime;
            });
          }
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [
    gameStatus,
    timerActive,
    gameOver,
    currentPlayer,
    playerColor,
    socket?.id,
  ]);

  // Handle timeout
  const handleTimeOut = (winnerColor) => {
    setGameOver(true);
    setGameStatus("finished");
    setTimerActive(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Set winner based on who ran out of time
    const winner = winnerColor === "w" ? "White" : "Black";
    setWinner(winner);

    // Calculate points - only if not already awarded
    if (!pointsAwarded) {
      if (winnerColor === playerColor) {
        // This player won (opponent ran out of time)
        const winPoints = userDetails?.earlyUsersLimitReached ? 1000 : 2000;
        setPoints(winPoints);
        handleApiResponse(winPoints.toString());
      } else {
        // This player lost (ran out of time) - no points
        const losePoints = 0;
        setPoints(losePoints);
        handleApiResponse(losePoints.toString());
      }
    }

    // Convert winner color to socket ID for backend
    let winnerSocketId = null;
    if (winnerColor) {
      const winnerPlayerIndex = winnerColor === "w" ? 0 : 1;
      winnerSocketId = players[winnerPlayerIndex];
    }

    // Calculate points for timeout scenario
    const winnerPoints = userDetails?.earlyUsersLimitReached ? 1000 : 2000;
    const loserPoints = 0;

    // Emit timeout to server
    socket.emit("updateChessGameState", {
      roomId: roomId,
      fen: game.fen(),
      players: players,
      currentPlayer: currentPlayer,
      gameStatus: "finished",
      winner: winnerSocketId, // Send socket ID instead of color
      timeout: true,
      points: {
        winner: winnerPoints,
        loser: loserPoints,
        winnerSocketId: winnerSocketId,
        loserSocketId: winnerSocketId
          ? players.find((id) => id !== winnerSocketId)
          : null,
      },
    });
  };

  // Update the board state from the chess.js instance
  const updateBoard = (currentGame = game) => {
    const newBoard = [];

    // If player is black, flip the board so black pieces appear at bottom
    const isBlackPlayer = playerColor === "b";

    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        let square, displayRow, displayCol;

        if (isBlackPlayer) {
          // Flip the board for black player - black pieces at bottom
          const flippedRow = 7 - i; // Flip rows (0 becomes 7, 1 becomes 6, etc.)
          const flippedCol = 7 - j; // Flip columns (a becomes h, b becomes g, etc.)
          square = String.fromCharCode(97 + flippedCol) + (flippedRow + 1);
          // Use flipped coordinates for display
          displayRow = flippedRow;
          displayCol = flippedCol;
        } else {
          // Normal orientation for white player - white pieces at bottom
          square = String.fromCharCode(97 + j) + (8 - i);
          // Use normal coordinates for display
          displayRow = i;
          displayCol = j;
        }

        const piece = currentGame.get(square);
        newBoard.push({
          square,
          piece,
          row: displayRow, // Use flipped coordinates for CSS styling
          col: displayCol,
        });
      }
    }
    setBoard(newBoard);
    updateGameStatus(currentGame);
    trackCapturedPieces(currentGame);
    updateMovablePieces(currentGame);
  };

  // Update which pieces can be moved
  const updateMovablePieces = (currentGame = game) => {
    // Only calculate movable pieces if it's the player's turn
    if (currentGame.turn() === playerColor && !gameOver && isMyTurn) {
      const squares = [];
      // Get the current board state directly from chess.js
      const currentBoard = currentGame.board();

      // Loop through all squares on the board
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 8; j++) {
          const piece = currentBoard[i][j];
          // Check if there's a piece of the player's color on this square
          if (piece && piece.color === playerColor) {
            // Convert board coordinates to algebraic notation (e.g., "e4")
            const square = String.fromCharCode(97 + j) + (8 - i);
            // Check if this piece has any legal moves
            const moves = currentGame.moves({
              square: square,
              verbose: true,
            });
            if (moves.length > 0) {
              squares.push(square);
            }
          }
        }
      }
      setMovablePieces(squares);
    } else {
      setMovablePieces([]);
    }
  };

  // Track captured pieces
  const trackCapturedPieces = (currentGame = game) => {
    const whitePieces = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };
    const blackPieces = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };

    // Count remaining pieces on the board
    const currentBoard = currentGame.board();
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const piece = currentBoard[i][j];
        if (piece) {
          if (piece.color === "w") {
            whitePieces[piece.type]--;
          } else {
            blackPieces[piece.type]--;
          }
        }
      }
    }

    // Convert to arrays of captured pieces
    const whiteCaptured = [];
    const blackCaptured = [];

    Object.entries(whitePieces).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        blackCaptured.push(type);
      }
    });

    Object.entries(blackPieces).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        whiteCaptured.push(type);
      }
    });

    setCapturedPieces({
      w: whiteCaptured,
      b: blackCaptured,
    });
  };

  // Update game status
  const updateGameStatus = (currentGame = game) => {
    if (currentGame.isCheckmate()) {
      setStatus(`${currentGame.turn() === "w" ? "Black" : "White"} wins!`);
      if (gameStatus !== "finished") {
        const winner = currentGame.turn() === "w" ? "b" : "w";

        // Only emit game over if we're in an active multiplayer game
        if (socket && roomId && gameStatus === "playing") {
          // Convert winner color to socket ID for backend
          const winnerPlayerIndex = winner === "w" ? 0 : 1;
          const winnerSocketId = players[winnerPlayerIndex];

          // Calculate points for checkmate scenario
          const winnerPoints = userDetails?.earlyUsersLimitReached
            ? 1000
            : 2000;
          const loserPoints = 0;

          socket.emit("updateChessGameState", {
            fen: currentGame.fen(),
            players,
            currentPlayer: currentPlayer,
            gameStatus: "finished",
            roomId,
            winner: winnerSocketId, // Send socket ID instead of color
            lastMove,
            points: {
              winner: winnerPoints,
              loser: loserPoints,
              winnerSocketId: winnerSocketId,
              loserSocketId: winnerSocketId
                ? players.find((id) => id !== winnerSocketId)
                : null,
            },
          });
        }
      }
    } else if (currentGame.isDraw()) {
      setStatus("Game over! It's a draw");
      if (gameStatus !== "finished") {
        // Only emit game over if we're in an active multiplayer game
        if (socket && roomId && gameStatus === "playing") {
          socket.emit("updateChessGameState", {
            fen: currentGame.fen(),
            players,
            currentPlayer: currentPlayer,
            gameStatus: "finished",
            roomId,
            winner: "Draw", // Draw is already a string, no conversion needed
            lastMove,
            points: {
              winner: 0,
              loser: 0,
              winnerSocketId: null,
              loserSocketId: null,
            },
          });
        }
      }
    } else if (currentGame.isCheck()) {
      setStatus(
        `${currentGame.turn() === "w" ? "White" : "Black"} to move - Check!`
      );
    } else {
      setStatus(`${currentGame.turn() === "w" ? "White" : "Black"} to move`);
    }
  };

  // Handle API response for points
  const handleApiResponse = async (points) => {
    // Prevent multiple API calls for the same game using ref for immediate check
    if (pointsAwarded || apiCallInProgress.current) {
      return;
    }

    // Set flag immediately to prevent concurrent calls
    apiCallInProgress.current = true;

    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "ChessWeb",
      };

      const res = await userGameRewards(apiData);

      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        // Mark points as awarded
        setPointsAwarded(true);
      }
    } catch (error) {
      // API call failed
    } finally {
      // Reset the flag after API call completes
      apiCallInProgress.current = false;
    }
  };

  // Handle square click
  const handleSquareClick = (square) => {
    if (gameOver || !isMyTurn || gameStatus !== "playing") return;

    // If a square is already selected
    if (selectedSquare) {
      // Try to make a move
      try {
        const move = game.move({
          from: selectedSquare,
          to: square.square,
          promotion: "q", // Always promote to queen for simplicity
        });

        if (move) {
          // Update last move
          const newLastMove = {
            from: move.from,
            to: move.to,
            color: playerColor,
          };
          setLastMove(newLastMove);

          // Find next player
          const nextPlayer = players.find((id) => id !== currentPlayer);

          // Update local game state
          updateBoard();
          setSelectedSquare(null);
          setValidMoves([]);
          setIsMyTurn(false);

          // Switch timer to next player
          if (playerColor === "w") {
            // White just moved, switch to black's timer
            setTimerActive(false);
            setTimeout(() => setTimerActive(true), 100); // Small delay to ensure clean switch
          } else {
            // Black just moved, switch to white's timer
            setTimerActive(false);
            setTimeout(() => setTimerActive(true), 100); // Small delay to ensure clean switch
          }

          // Convert winner color to socket ID for backend
          let winnerSocketId = null;
          if (game.isCheckmate()) {
            // Find the socket ID of the player who has the winning color
            const winnerPlayerIndex = playerColor === "w" ? 0 : 1;
            winnerSocketId = players[winnerPlayerIndex];
          } else if (game.isDraw()) {
            winnerSocketId = "Draw";
          }

          // Create the updated game state
          // Calculate points for each player
          let winnerPoints = 0;
          let loserPoints = 0;
          if (game.isCheckmate()) {
            // Someone won
            winnerPoints = userDetails?.earlyUsersLimitReached ? 1000 : 2000;
            loserPoints = 0;
          } else if (game.isDraw()) {
            // Draw
            winnerPoints = 0;
            loserPoints = 0;
          }

          const updatedGameState = {
            fen: game.fen(),
            players,
            currentPlayer: nextPlayer,
            gameStatus:
              game.isCheckmate() || game.isDraw() ? "finished" : "playing",
            roomId,
            winner: winnerSocketId, // Send socket ID instead of color
            lastMove: newLastMove,
            whiteTime: whiteTime,
            blackTime: blackTime,
            points: {
              winner: winnerPoints,
              loser: loserPoints,
              winnerSocketId: winnerSocketId,
              loserSocketId: winnerSocketId
                ? players.find((id) => id !== winnerSocketId)
                : null,
            },
          };

          // Send updated game state to server
          socket.emit("updateChessGameState", updatedGameState);
        } else {
          // Invalid move, just update selection
          updateSelection(square);
        }
      } catch (e) {
        // Invalid move, just update selection
        updateSelection(square);
      }
    } else {
      // No square selected yet, select this one if it has a piece
      updateSelection(square);
    }
  };

  // Update square selection
  const updateSelection = (square) => {
    if (square.piece && square.piece.color === playerColor) {
      setSelectedSquare(square.square);

      // Find valid moves
      const moves = game.moves({
        square: square.square,
        verbose: true,
      });

      setValidMoves(moves.map((move) => move.to));
    } else {
      setSelectedSquare(null);
      setValidMoves([]);
    }
  };

  // Copy room ID to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch (err) {
      setCopySuccess("Failed to copy");
    }
  };

  // Generate a 6-character alphanumeric room ID
  const generateRoomId = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Removed confusing characters like 0/O, 1/I
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Create a new game room
  const startNewGame = () => {
    const newRoomId = generateRoomId();
    // Reset all game states to initial values
    setGame(new Chess());
    setBoard([]);
    setSelectedSquare(null);
    setValidMoves([]);
    setGameStatus("waiting");
    setWinner(null);
    setGameOver(false);
    setLastMove(null);
    setPoints(0);
    setPointsAwarded(false);
    apiCallInProgress.current = false;
    setRoomId(newRoomId);
    socket.emit("joinRoom", { roomId: newRoomId, gameType: "chess" });
    updateBoard();
  };

  // Join an existing game room
  const joinGame = (roomIdToJoin) => {
    if (!roomIdToJoin) {
      alert("Please enter a Room ID");
      return;
    }

    // Validate room ID format (must be 6 alphanumeric characters)
    if (!/^[A-Z0-9]{6}$/.test(roomIdToJoin.toUpperCase())) {
      alert("Room ID must be 6 alphanumeric characters");
      return;
    }

    // Check if trying to join the same room we're already in
    if (roomId === roomIdToJoin) {
      // Already in this room, no need to rejoin
      return;
    }
    setGame(new Chess());
    setBoard([]);
    setSelectedSquare(null);
    setValidMoves([]);
    setGameStatus("waiting");
    setWinner(null);
    setGameOver(false);
    setLastMove(null);
    setPoints(0);
    setPointsAwarded(false);
    apiCallInProgress.current = false;
    socket.emit("joinRoom", { roomId: roomIdToJoin, gameType: "chess" });
    updateBoard();
  };

  // Reset game for a new match
  const startOverNewGame = () => {
    // Reset all game states to initial values
    setGame(new Chess());
    setBoard([]);
    setSelectedSquare(null);
    setValidMoves([]);
    setGameStatus("waiting");
    setWinner(null);
    setGameOver(false);
    setLastMove(null);
    setPoints(0);
    setPointsAwarded(false);
    apiCallInProgress.current = false;
    setRoomId(null); // Reset roomId to null to show initial screen
    setPlayerColor(null); // Reset player color
    setInputRoomId("");
    setShowTimeoutPopup(false);
    setPlayers([]);
    setCurrentPlayer(null);

    // Reset timers
    setWhiteTime(600);
    setBlackTime(600);
    setTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    updateBoard();
  };

  // Go back to menu or cancel current game
  const toogleMenu = () => {
    playClick();

    // If room is created but no players have joined yet, go back to multiplayer options
    if (roomId && gameStatus === "waiting" && players.length < 2) {
      if (socket && roomId) {
        socket.emit("leaveRoom", { roomId: roomId });
      }
      setRoomId(null);
      setGameStatus("multiplayer");
      setGameOver(false);
      setWinner(null);
      setTimerActive(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return;
    }

    if (
      roomId &&
      gameStatus === "playing" &&
      !gameOver &&
      !pointsAwarded &&
      players.length === 2
    ) {
      // Award points to opponent and show game over popup (only if both players connected and actively playing)
      const opponentWinPoints = userDetails?.earlyUsersLimitReached
        ? 1000
        : 2000;
      setPoints(opponentWinPoints);
      handleApiResponse(opponentWinPoints.toString());

      // Emit game state to notify opponent
      if (socket && roomId) {
        const opponentSocketId = players.find((id) => id !== socket.id);

        // Calculate points for back button scenario (opponent wins)
        const opponentWinPoints = userDetails?.earlyUsersLimitReached
          ? 1000
          : 2000;
        const currentPlayerPoints = 0;

        const updatedGameState = {
          fen: game.fen(),
          players,
          currentPlayer: opponentSocketId,
          gameStatus: "finished",
          roomId,
          winner: opponentSocketId, // Opponent wins
          lastMove,
          whiteTime: whiteTime,
          blackTime: blackTime,
          points: {
            winner: opponentWinPoints,
            loser: currentPlayerPoints,
            winnerSocketId: opponentSocketId,
            loserSocketId: socket.id,
          },
        };
        socket.emit("updateChessGameState", updatedGameState);
        socket.emit("leaveRoom", { roomId: roomId });
      }

      // Set game over state to show points popup briefly, then navigate back
      setWinner("Opponent");
      setGameStatus("finished");
      setGameOver(true);
      setTimerActive(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Navigate back immediately after setting game state
      updateUserInfo((prev) => ({
        ...prev,
        isPlay: false,
        currentComponent: task,
        currentComponentText: "task",
        lastComponent: userDetails?.userDetails.currentComponent,
        lastComponentText: userDetails?.userDetails.currentComponentText,
        isMenu: true,
        menuCount: userDetails?.userDetails?.menuCount + 1,
      }));
    } else if (roomId) {
      // Game is finished, waiting, only one player, or points already awarded - just leave without awarding points

      if (socket && roomId) {
        socket.emit("leaveRoom", { roomId: roomId });
      }
      setRoomId(null);
      setGameStatus("waiting");
      setGameOver(false);
      setWinner(null);
      setTimerActive(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    } else {
      updateUserInfo((prev) => ({
        ...prev,
        isPlay: false,
        currentComponent: task,
        currentComponentText: "task",
        lastComponent: userDetails?.userDetails.currentComponent,
        lastComponentText: userDetails?.userDetails.currentComponentText,
        isMenu: true,
        menuCount: userDetails?.userDetails?.menuCount + 1,
      }));
    }
  };

  // Navigate to single player chess
  const navigateToSinglePlayer = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: true,
      currentComponent: require("../chess/chess").default,
      currentComponentText: "chess",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
    }));
  };

  // Initialize board on component mount
  useEffect(() => {
    updateBoard();
  }, []);

  return (
    <div className="chess-container" style={{ pointerEvents: "fill" }}>
      {/* Main Back Button - Only show on landing page */}
      {!roomId && gameStatus === "waiting" && (
        <img
          onClick={toogleMenu}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-imgpointschess"
          style={{ cursor: "pointer" }}
        />
      )}
      {/* Info Button */}
      <button
        onClick={() => setShowInfoPopup(true)}
        className="chess-info-button"
        title="Game Info"
      >
        ℹ️
      </button>
      {/* Landing UI - Show when no room is active */}
      {!roomId && gameStatus === "waiting" && (
        <div className="chess-landing">
          {/* Chess Logo */}
          <div className="chess-logo">
            <div className="chess-logo-icon">
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/chess.png"
                alt="chesslogo"
                style={{ width: "150px", borderRadius: "10px" }}
              />
            </div>
            {/* <h1 className="chess-logo-text">CHESS</h1> */}
            <p className="chess-logo-subtitle">Strategic Battle of Minds</p>
          </div>

          {/* Game Mode Buttons */}
          <div className="chess-game-modes">
            <button
              onClick={navigateToSinglePlayer}
              className="chess-mode-button computer-mode"
            >
              <div className="mode-icon">🖥️</div>
              <div className="mode-content">
                <h3>Play With The Computer</h3>
                <p>Challenge THEMEMETV </p>
              </div>
            </button>

            <button
              onClick={() => setGameStatus("multiplayer")}
              className="chess-mode-button multiplayer-mode"
            >
              <div className="mode-icon">👥</div>
              <div className="mode-content">
                <h3>Multiplayer</h3>
                <p>Play with friends online</p>
              </div>
            </button>
          </div>
        </div>
      )}
      {/* Multiplayer Options - Show when multiplayer is selected */}
      {gameStatus === "multiplayer" && !roomId && (
        <div className="chess-multiplayer-options">
          <div className="multiplayer-header">
            <button
              onClick={() => setGameStatus("waiting")}
              className="back-buttonmenu text-uppercase"
            >
              ← Back
            </button>
            <h2>Multiplayer Chess</h2>
          </div>

          <div className="multiplayer-options">
            <button
              onClick={startNewGame}
              className="multiplayer-option create-game"
            >
              <div className="option-icon">➕</div>
              <div className="option-content">
                <h3>Create New Game</h3>
                <p>Start a new room and invite friends</p>
              </div>
            </button>

            <div className="join-game-section">
              <h3>Join Existing Game</h3>
              <div className="join-game-input">
                <input
                  type="text"
                  placeholder="Enter 6-character Room ID"
                  value={inputRoomId}
                  maxLength={6}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase();
                    setInputRoomId(value);
                  }}
                />
                <button
                  onClick={() => joinGame(inputRoomId)}
                  className="join-button"
                >
                  Join
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Original Game UI - Show when in a room */}
      {roomId && (
        <div className="col-12">
          <div className="game-header">
            <button onClick={handleBackButton} className="game-back-button">
              ← Back
            </button>

            <h2 class="chess-logo-text text-center text-uppercase w-100">
              Multiplayer
            </h2>
          </div>
        </div>
      )}
      {roomId && gameStatus === "waiting" && (
        <div className="multiplayer-chess-waiting-room">
          <h3>Room ID:</h3>
          <div className="multiplayer-chess-room-id-container">
            <input
              type="text"
              value={roomId}
              readOnly
              className="multiplayer-chess-room-id-display"
            />
            <button
              className="multiplayer-chess-copy-button"
              onClick={() => copyToClipboard(roomId)}
            >
              {copySuccess || "Copy"}
            </button>
          </div>
          <p>Share this Room ID with your friend to play together</p>
          <p>Wait till the other player to joins...</p>
        </div>
      )}
      x
      {gameStatus === "playing" && (
        <>
          {/* Opponent Captured Pieces - Top of Board */}
          <div className="captured-pieces ai-captured">
            <div className="captured-info">
              <div className="captured-pieces-content">
                <strong className="text-uppercase">
                  Captured by {playerColor === "w" ? "Black" : "White"}:
                </strong>
                <div className="captured-pieces-row">
                  {(playerColor === "w"
                    ? capturedPieces.b
                    : capturedPieces.w
                  ).map((piece, index) => (
                    <span key={index} className="captured-piece">
                      {playerColor === "w" ? PIECES.w[piece] : PIECES.b[piece]}
                    </span>
                  ))}
                </div>
              </div>

              <div className="captured-timer">
                <div
                  className={`chess-timer ${
                    isMyTurn && playerColor === "b" ? "active" : ""
                  }`}
                >
                  <span>
                    {playerColor === "w" ? "Black" : "White"}:{" "}
                    {Math.floor(
                      playerColor === "w" ? blackTime / 60 : whiteTime / 60
                    )}
                    :
                    {(playerColor === "w" ? blackTime % 60 : whiteTime % 60)
                      .toString()
                      .padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Chess Board */}
          <div className="chess-board">
            {board.map((square, index) => (
              <div
                key={index}
                style={{
                  gridRow: `${square.row + 1}`,
                  gridColumn: `${square.col + 1}`,
                }}
                className={`chess-square ${
                  (square.row + square.col) % 2 === 0
                    ? "chess-square-white"
                    : "chess-square-black"
                } ${
                  selectedSquare === square.square
                    ? "chess-square-selected"
                    : ""
                } ${
                  validMoves.includes(square.square)
                    ? "chess-square-valid-move"
                    : ""
                } ${
                  lastMove && lastMove.from === square.square
                    ? "last-move-from"
                    : ""
                } ${
                  lastMove && lastMove.to === square.square
                    ? "last-move-to"
                    : ""
                }`}
                onClick={() => handleSquareClick(square)}
              >
                {square.piece && (
                  <div
                    className={`chess-piece ${
                      square.piece.color === "w" ? "white-piece" : "black-piece"
                    } ${
                      movablePieces.includes(square.square)
                        ? "movable-piece"
                        : ""
                    }`}
                    data-is-movable={
                      movablePieces.includes(square.square) ? "true" : "false"
                    }
                  >
                    {PIECES[square.piece.color][square.piece.type]}
                  </div>
                )}
              </div>
            ))}
            {(game.isCheck() || game.isCheckmate()) && (
              <div className="chess-status-overlay">
                <div className="chess-status-message">
                  {game.isCheckmate() ? "CHECKMATE!" : "CHECK!"}
                </div>
              </div>
            )}
          </div>

          {/* Player Captured Pieces - Bottom of Board */}
          <div className="captured-pieces player-captured">
            <div className="captured-info">
              <div className="captured-pieces-content">
                <strong className="text-uppercase">
                  Captured by {playerColor === "w" ? "White" : "Black"}:
                </strong>
                <div className="captured-pieces-row">
                  {(playerColor === "w"
                    ? capturedPieces.w
                    : capturedPieces.b
                  ).map((piece, index) => (
                    <span key={index} className="captured-piece">
                      {playerColor === "w" ? PIECES.b[piece] : PIECES.w[piece]}
                    </span>
                  ))}
                </div>
              </div>

              <div className="captured-timer">
                <div
                  className={`chess-timer ${
                    isMyTurn && playerColor === "w" ? "active" : ""
                  }`}
                >
                  <span>
                    {playerColor === "w" ? "White" : "Black"}:{" "}
                    {Math.floor(
                      playerColor === "w" ? whiteTime / 60 : blackTime / 60
                    )}
                    :
                    {(playerColor === "w" ? whiteTime % 60 : blackTime % 60)
                      .toString()
                      .padStart(2, "0")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {gameOver && (
        <div className="multiplayer-chess-overlay">
          <div className="multiplayer-chess-popup">
            <div className="multiplayer-chess-popup-content">
              <h2 className="multiplayer-chess-epic">Game Over!</h2>
              <div className="row text-center">
                <div className="col-12">
                  <div
                    className="multiplayer-chess-epic-div"
                    style={{ padding: "30px 40px" }}
                  >
                    <p className="multiplayer-chess-points-label">
                      TOKENS EARNED
                    </p>
                    <div className="multiplayer-chess-points-value">
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="logo"
                      />
                      <p className="multiplayer-chess-points-number">
                        {points}
                      </p>
                    </div>
                  </div>
                  <p className="multiplayer-chess-winner-text">
                    {winner === "Draw"
                      ? "It's a draw!"
                      : winner === playerColor
                      ? "You won!"
                      : winner === "Disconnected"
                      ? "Opponent Disconnected"
                      : "Opponent won!"}
                  </p>
                </div>
              </div>
            </div>
            <div className="multiplayer-chess-playagain">
              <button
                className="multiplayer-chess-playgame"
                onClick={startOverNewGame}
              >
                START NEW GAME
              </button>
            </div>
          </div>
        </div>
      )}
      {showTimeoutPopup && (
        <div className="multiplayer-chess-overlay">
          <div className="multiplayer-chess-popup">
            <div className="multiplayer-chess-popup-content">
              <h2 className="multiplayer-chess-epic">No Player Joined</h2>
              <p>It seems no one has joined your game within the time limit.</p>
              <p>What would you like to do?</p>
            </div>
            <div className="multiplayer-chess-playagain">
              <button
                className="multiplayer-chess-playgame"
                onClick={startOverNewGame}
              >
                RETURN TO MENU
              </button>
              <button
                className="multiplayer-chess-playgame"
                onClick={navigateToSinglePlayer}
              >
                PLAY WITH COMPUTER
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Info Popup */}
      {showInfoPopup && (
        <div className="chess-info-overlay">
          <div className="chess-info-popup">
            <div className="chess-info-header">
              <h3>Chess Game Info</h3>
              <button
                onClick={() => setShowInfoPopup(false)}
                className="chess-info-close"
              >
                ✕
              </button>
            </div>

            <div className="chess-info-content">
              <div className="info-section">
                <h4>🎯 How to Play</h4>
                <ul>
                  <li>Create a room or join with a 6-character Room ID</li>
                  <li>White player moves first</li>
                  <li>Each player has 10 minutes to complete the game</li>
                  <li>
                    Click on a piece to select it, then click on a valid square
                    to move
                  </li>
                  <li>Capture opponent pieces to gain advantage</li>
                  <li>Checkmate the opponent's king to win</li>
                </ul>
              </div>

              <div className="info-section">
                <h4>💰 Points System</h4>
                <ul>
                  <li>
                    <strong>Win by Checkmate:</strong> 1000-2000 $MEMETV TOKENS
                  </li>
                  <li>
                    <strong>Win by Timeout:</strong> 1000-2000 $MEMETV TOKENS
                  </li>
                  <li>
                    <strong>Win by Disconnect:</strong> 1000-2000 $MEMETV TOKENS
                  </li>
                  <li>
                    <strong>Lose:</strong> 0-500 $MEMETV TOKENS
                  </li>
                  <li>
                    <strong>Draw:</strong> 500-1000 $MEMETV TOKENS
                  </li>
                </ul>
                <p className="points-note">
                  ** Early users get higher $MEMETV TOKENS
                </p>
              </div>
              <div className="info-section">
                <h4>⚡ Game Features</h4>
                <ul>
                  <li>Real-time multiplayer gameplay</li>
                  <li>10-minute timer per player</li>
                  <li>Captured pieces display</li>
                  <li>Next Move Validation & Highlight</li>
                  <li>Responsive design for all devices</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Leave Game Confirmation Dialog */}
      {showLeaveConfirmation && (
        <div className="multiplayer-chess-overlay">
          <div className="multiplayer-chess-popup">
            <div className="multiplayer-chess-popup-content">
              <h3 className="multiplayer-chess-epic">Leave Game?</h3>
              <p className="multiplayer-chess-winner-text">
                Are you sure you want to leave the game? Your opponent will win
                by default.
              </p>
            </div>
            <div className="multiplayer-chess-playagain">
              <button
                className="multiplayer-chess-playgame"
                onClick={leaveGame}
              >
                Yes, Leave Game
              </button>
              <button
                className="multiplayer-chess-playgame"
                onClick={() => setShowLeaveConfirmation(false)}
                style={{ background: "#666", border: "2px solid #666" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChessGameWeb;
